/**
 * 组织选择弹窗
 * 用于登录后选择当前工作组织（当用户属于多个组织时）
 */

import React, { useState, useEffect } from 'react';
import { Modal, List, Avatar, Tag, Spin, Typography, Space } from 'antd';
import { message } from '@/utils/antdStatic';
import {
  ApartmentOutlined,
  CheckCircleFilled,
  StarFilled,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getMyOrganizations,
  switchOrganization,
  getRelationTypeLabel,
  type MyOrganization,
  type RelationType,
} from '@/api/organization';
import { STORAGE_KEYS, setStorageItem } from '@/utils/storage';
import './index.less';

const { Text, Title } = Typography;

interface OrganizationSelectModalProps {
  /** 是否显示弹窗 */
  open: boolean;
  /** 选择成功后的回调 */
  onSuccess?: (orgId: number, orgName: string, newToken: string) => void;
  /** 关闭弹窗的回调 */
  onClose?: () => void;
  /** 是否允许关闭（首次登录必须选择时为false） */
  closable?: boolean;
}

/**
 * 组织选择弹窗组件
 */
const OrganizationSelectModal: React.FC<OrganizationSelectModalProps> = ({
  open,
  onSuccess,
  onClose,
  closable = true,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [organizations, setOrganizations] = useState<MyOrganization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);

  // 加载组织列表
  const loadOrganizations = async () => {
    setLoading(true);
    try {
      const result = await getMyOrganizations();
      if (result && result.items) {
        setOrganizations(result.items);
        // 默认选中主组织或第一个
        const primaryOrg = result.items.find((org) => (org as any).isPrimary ?? org.is_primary);
        if (primaryOrg) {
          setSelectedOrgId(primaryOrg.id);
        } else if (result.items.length > 0) {
          setSelectedOrgId(result.items[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
      message.error(t('organization.loadFailed', 'Failed to load organizations'));
    } finally {
      setLoading(false);
    }
  };

  // 弹窗打开时加载组织列表
  useEffect(() => {
    if (open) {
      loadOrganizations();
    }
  }, [open]);

  // 处理组织选择
  const handleSelect = (orgId: number) => {
    setSelectedOrgId(orgId);
  };

  // 确认选择
  const handleConfirm = async () => {
    if (!selectedOrgId) {
      message.warning(t('organization.selectRequired', 'Please select an organization'));
      return;
    }

    setSelecting(true);
    try {
      const result = await switchOrganization(selectedOrgId);
      if (result && result.token) {
        // 更新Token
        setStorageItem(STORAGE_KEYS.TOKEN, result.token);

        const selectedOrg = organizations.find((org) => org.id === selectedOrgId);
        const currentOrg = (result as any).currentOrg ?? result.current_org;
        message.success(t('organization.switchSuccess', { name: currentOrg?.name }));

        // 触发成功回调
        onSuccess?.(selectedOrgId, currentOrg?.name, result.token);
      } else {
        message.error(t('organization.switchFailed', 'Failed to switch organization'));
      }
    } catch (error) {
      console.error('Failed to switch organization:', error);
      message.error(t('organization.switchFailed', 'Failed to switch organization'));
    } finally {
      setSelecting(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <ApartmentOutlined />
          {t('organization.selectTitle', 'Select Your Organization')}
        </Space>
      }
      open={open}
      onOk={handleConfirm}
      onCancel={closable ? onClose : undefined}
      closable={closable}
      maskClosable={closable}
      keyboard={closable}
      okText={t('common.confirm', 'Confirm')}
      cancelText={t('common.cancel', 'Cancel')}
      cancelButtonProps={{ style: closable ? {} : { display: 'none' } }}
      confirmLoading={selecting}
      width={500}
      className="org-select-modal"
    >
      <div className="org-select-content">
        <Text type="secondary" className="org-select-hint">
          {t('organization.selectHint', 'Please select the organization you want to work with')}
        </Text>

        {loading ? (
          <div className="org-select-loading">
            <Spin size="large" />
          </div>
        ) : (
          <List
            className="org-select-list"
            dataSource={organizations}
            renderItem={(org) => (
              <List.Item
                className={`org-select-item ${selectedOrgId === org.id ? 'selected' : ''}`}
                onClick={() => handleSelect(org.id)}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      icon={<ApartmentOutlined />}
                      style={{
                        backgroundColor: selectedOrgId === org.id ? '#1890ff' : '#f0f0f0',
                        color: selectedOrgId === org.id ? '#fff' : '#666',
                      }}
                    />
                  }
                  title={
                    <Space>
                      <Text strong={selectedOrgId === org.id}>
                        {(org as any).shortName || org.short_name || org.name}
                      </Text>
                      {((org as any).isPrimary ?? org.is_primary) && (
                        <Tag icon={<StarFilled />} color="gold">
                          {t('organization.primary', 'Primary')}
                        </Tag>
                      )}
                    </Space>
                  }
                  description={
                    <Space size={4} wrap>
                      {org.code && <Text type="secondary">{org.code}</Text>}
                      {((org as any).relationTypes ?? org.relation_types)?.map((type: RelationType) => (
                        <Tag key={type} color="blue" style={{ fontSize: 10 }}>
                          {getRelationTypeLabel(type)}
                        </Tag>
                      ))}
                    </Space>
                  }
                />
                {selectedOrgId === org.id && (
                  <CheckCircleFilled style={{ color: '#1890ff', fontSize: 20 }} />
                )}
              </List.Item>
            )}
          />
        )}
      </div>
    </Modal>
  );
};

export default OrganizationSelectModal;
